import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { FIELD_META_MAP } from 'chameleon/src/components/assign/AssignManageForm/utils/constants';
import AutocompleteSelectField from 'chameleon/legacy/src/components/assignment/Assignment/components/Actions/CustomAssesmentSettings/Modal/Form/AutocompleteSelectField';
import TextInputField from 'chameleon/legacy/src/components/assignment/Assignment/components/Actions/CustomAssesmentSettings/Modal/Form/TextInputField';
import { useFormContext } from 'react-hook-form';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import DropdownMenuIcon from 'chameleon/legacy/src/components/assessment/AssignItem/components/AssignModal/Form/ClassesStudentsSelectDropdown/DropdownMenuIcon';

export const FIELD_NAMES = {
  STUDENT_SCORING: 'student_scoring',
  CUSTOM_ASSIGNMENT_NAME: 'title',
  SCRAMBLE_QUESTIONS: 'scramble_questions',
  SCRAMBLE_QUESTIONS_PER_STUDENT: 'scramble_questions_per_student',
  LOCKDOWN_BROWSER: 'is_secure',
  IMMEDIATE_FEEDBACK: 'has_immediate_feedback',
  IMMEDIATE_FEEDBACK_ATTEMPTS: 'immediate_feedback_attempts',
};

const AdvancedSettings = ({
  id,
  hasImmediateFeedbackFF,
  hasScramblePerStudentFF,
  hasSecureBrowserFF,
  hasFrqQuestions,
  hasMcqQuestions,
  isPPC,
  isPrint,
  hasAudioRecordOrFileUpload,
  isPracticeExam,
}) => {
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const { watch } = useFormContext();

  const isOnline = !isPrint;

  const isScrableQuestionsDisabled = isPracticeExam;
  const isStudentScoringEnabled = watch(`${id}.${FIELD_NAMES.STUDENT_SCORING}`);
  const isLdbEnabled = watch(`${id}.${FIELD_NAMES.LOCKDOWN_BROWSER}`);

  const isImmediateFeedbackEnabled = watch(
    `${id}.${FIELD_NAMES.IMMEDIATE_FEEDBACK}`,
  );
  const isScrambledOrder = watch(`${id}.${FIELD_NAMES.SCRAMBLE_QUESTIONS}`);
  const customAssignmentName = watch(`${id}.${FIELD_NAMES.SCRAMBLE_QUESTIONS}`);

  const showLdbField = isOnline && hasSecureBrowserFF;
  const enableSecureBrowserOnPurpose = true; // More details in FY-20224
  const isLdbFieldDisabled =
    hasAudioRecordOrFileUpload || !enableSecureBrowserOnPurpose;
  const showStudentScoringField = hasFrqQuestions;
  const showImmediateFeedbackField =
    hasImmediateFeedbackFF && isOnline && hasMcqQuestions;

  const isAdvancedSettingsOpenedByDefault = Boolean(
    isLdbEnabled ||
      customAssignmentName ||
      isScrambledOrder ||
      isStudentScoringEnabled ||
      isImmediateFeedbackEnabled,
  );

  useEffect(() => {
    if (isAdvancedSettingsOpenedByDefault && !showAdvancedSettings) {
      setShowAdvancedSettings(isAdvancedSettingsOpenedByDefault);
    }
  }, [isAdvancedSettingsOpenedByDefault]);

  const toggleAdvancedSettings = () => {
    setShowAdvancedSettings(!showAdvancedSettings);
  };

  return (
    <Fragment>
      <div className="flex items-center relative">
        <ButtonWithIcon
          accessibility={{
            'aria-expanded': showAdvancedSettings,
          }}
          iconAccessibility={{ ariaHidden: 'true' }}
          className="expand-advanced-setitngs bg-gray-200 mb-6 !p-0"
          icon="cog"
          iconWidth={24}
          iconHeight={24}
          onClick={toggleAdvancedSettings}
        >
          <span className="text-16 font-medium">Advanced Settings</span>
        </ButtonWithIcon>
        <DropdownMenuIcon
          onClick={toggleAdvancedSettings}
          className={`h-full w-auto cursor-pointer ${showAdvancedSettings ? 'transform rotate-180 pr-4 pt-4' : 'pb-8 pl-4'}`}
          dataTestId="advanced-settings-dropdown-icon"
          iconId="selectbox_arrow_down"
          iconClassName="text-gray-600"
          accessibility={{ ariaHidden: 'true' }}
        />
      </div>
      {showAdvancedSettings && (
        <div className="flex gap-x-6">
          <div className="flex-1">
            {showStudentScoringField && (
              <div>
                <AutocompleteSelectField
                  data-test-id={`${id}-${FIELD_NAMES.STUDENT_SCORING}`}
                  name={`${id}.${FIELD_NAMES.STUDENT_SCORING}`}
                  title={FIELD_META_MAP.STUDENT_SCORING.title()}
                  infoPopupText={FIELD_META_MAP.STUDENT_SCORING.helpText()}
                  options={FIELD_META_MAP.STUDENT_SCORING.options}
                  placeholder={FIELD_META_MAP.STUDENT_SCORING.placeholder}
                />
                {isStudentScoringEnabled && (
                  <div className="-mt-4 mb-8 text-lg text-gray-600">
                    {FIELD_META_MAP.STUDENT_SCORING.detailsComponent()}
                  </div>
                )}
              </div>
            )}
            <TextInputField
              data-test-id={`${id}-${FIELD_NAMES.CUSTOM_ASSIGNMENT_NAME}`}
              name={`${id}.${FIELD_NAMES.CUSTOM_ASSIGNMENT_NAME}`}
              className="mb-8"
              icon={<span></span>}
              title={FIELD_META_MAP.CUSTOM_NAME.title({
                isResourceForm: false,
              })}
              infoPopupText={FIELD_META_MAP.CUSTOM_NAME.helpText()}
              placeholder="Enter Custom Name"
            />
            <AutocompleteSelectField
              data-test-id={`${id}-${FIELD_NAMES.SCRAMBLE_QUESTIONS}`}
              name={`${id}.${FIELD_NAMES.SCRAMBLE_QUESTIONS}`}
              title={FIELD_META_MAP.SCRAMBLE_QUESTIONS_PER_STUDENT.title()}
              infoPopupText={
                hasScramblePerStudentFF
                  ? FIELD_META_MAP.SCRAMBLE_QUESTIONS_PER_STUDENT.helpText()
                  : FIELD_META_MAP.SCRAMBLE_QUESTIONS.helpText()
              }
              options={
                hasScramblePerStudentFF
                  ? FIELD_META_MAP.SCRAMBLE_QUESTIONS_PER_STUDENT.options(
                      isPrint,
                    )
                  : FIELD_META_MAP.SCRAMBLE_QUESTIONS.options
              }
              placeholder={FIELD_META_MAP.SCRAMBLE_QUESTIONS.placeholder}
              disableDropdown={isScrableQuestionsDisabled}
            />
            <div className="-mt-4 text-lg text-gray-600" aria-live="polite">
              {isPPC
                ? FIELD_META_MAP.SCRAMBLE_QUESTIONS_PER_STUDENT.detailsComponent(
                    { value: isScrambledOrder },
                  )
                : null}
            </div>
          </div>
          <div className="flex-1">
            {showLdbField && (
              <div>
                <AutocompleteSelectField
                  data-test-id={`${id}-${FIELD_NAMES.LOCKDOWN_BROWSER}`}
                  name={`${id}.${FIELD_NAMES.LOCKDOWN_BROWSER}`}
                  title={FIELD_META_MAP.LOCKDOWN_BROWSER.title()}
                  infoPopupText={FIELD_META_MAP.LOCKDOWN_BROWSER.helpText()}
                  options={FIELD_META_MAP.LOCKDOWN_BROWSER.options}
                  placeholder={FIELD_META_MAP.LOCKDOWN_BROWSER.placeholder}
                  disableDropdown={isLdbFieldDisabled}
                />
                {isLdbEnabled && (
                  <div
                    id={FIELD_META_MAP.LOCKDOWN_BROWSER.ariaLabelledBy}
                    className="-mt-4 mb-6 text-lg text-gray-600"
                    aria-label={FIELD_META_MAP.LOCKDOWN_BROWSER.ariaLabel}
                    role="region"
                    aria-live="polite"
                  >
                    {FIELD_META_MAP.LOCKDOWN_BROWSER.detailsComponent()}
                  </div>
                )}
              </div>
            )}
            {showImmediateFeedbackField && (
              <div>
                <AutocompleteSelectField
                  data-test-id={`${id}-${FIELD_NAMES.IMMEDIATE_FEEDBACK}`}
                  name={`${id}.${FIELD_NAMES.IMMEDIATE_FEEDBACK}`}
                  title={FIELD_META_MAP.IMMEDIATE_FEEDBACK.title()}
                  infoPopupText={FIELD_META_MAP.IMMEDIATE_FEEDBACK.helpText()}
                  options={FIELD_META_MAP.IMMEDIATE_FEEDBACK.options}
                />
                {isImmediateFeedbackEnabled && (
                  <div role="region" aria-live="polite">
                    <AutocompleteSelectField
                      data-test-id={`${id}-${FIELD_NAMES.IMMEDIATE_FEEDBACK_ATTEMPTS}`}
                      name={`${id}.${FIELD_NAMES.IMMEDIATE_FEEDBACK_ATTEMPTS}`}
                      title={FIELD_META_MAP.IMMEDIATE_FEEDBACK_ATTEMPTS.title()}
                      infoPopupText={FIELD_META_MAP.IMMEDIATE_FEEDBACK_ATTEMPTS.helpText()}
                      options={
                        FIELD_META_MAP.IMMEDIATE_FEEDBACK_ATTEMPTS.options
                      }
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </Fragment>
  );
};

const mapStateToProps = (state) => {
  return {
    hasImmediateFeedbackFF: state.featureFlag.immediate_feedback,
    hasScramblePerStudentFF: state.featureFlag.scramble_questions_per_student,
    hasSecureBrowserFF: state.featureFlag.secure_browser,
  };
};

export default connect(mapStateToProps)(AdvancedSettings);
